import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { convertToAbsoluteGrade, getGradeOptions, getSchoolTypeFromName } from '../../utils/gradeUtils';
import { Modal, Button } from '../common';
import SchoolSearchInput from './SchoolSearchInput';
import SchoolNameUtils from '../../utils/schoolNameUtils';
import schoolList from '../../data/school_list.json';

function ObjectModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    school_name: '',
    att_year: '',
    school_grade: '',
    phase: '',
    subject: '',
    edu_version: '',
    school_type: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [examType, setExamType] = useState('school'); // 'school' or 'workbook'
  const [selectedSchool, setSelectedSchool] = useState(null);

  const phases = [
    { value: '11', label: '1학기 중간' },
    { value: '12', label: '1학기 기말' },
    { value: '13', label: '1학기 기타' },
    { value: '21', label: '2학기 중간' },
    { value: '22', label: '2학기 기말' },
    { value: '23', label: '2학기 기타' }
  ];

  const schoolTypes = [
    { value: '1', label: '초등학교' },
    { value: '2', label: '중학교' },
    { value: '3', label: '고등학교' }
  ];

  // 입력 모드 판단 (단일 vs 대량)
  const isBulkMode = () => {
    const { school_name, att_year, school_type } = formData;
    const { school_grade, phase } = formData;
    
    // 대량 생성 조건: school_name, att_year, school_type만 입력되고 학년, 시험구분은 비어있음
    // subject와 edu_version은 선택사항이므로 체크하지 않음
    return school_name && att_year && school_type && 
           !school_grade && !phase;
  };

  const isSingleMode = () => {
    const { school_name, att_year, school_grade, phase } = formData;
    // 단일 생성 조건: 필수 필드들이 모두 입력됨
    return school_name && att_year && school_grade && phase;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!isSingleMode() && !isBulkMode()) {
      alert('단일 생성을 원할 경우: 학교명, 출제년도, 학년, 시험구분을 입력하세요.\n대량 생성을 원할 경우: 학교명, 출제년도, 학교타입만 입력하세요.');
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      let url, requestData;

      if (isBulkMode()) {
        // 대량 생성
        url = `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/register/bulk`;
        requestData = {
          school_type: parseInt(formData.school_type),
          school_name: formData.school_name,
          att_year: parseInt(formData.att_year)
        };
      } else {
        // 단일 생성
        url = `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/register`;
        requestData = {
          school_name: formData.school_name,
          att_year: parseInt(formData.att_year),
          school_grade: convertToAbsoluteGrade(
            formData.school_type || getSchoolTypeFromName(formData.school_name), 
            formData.school_grade
          ),
          phase: parseInt(formData.phase),
          subject: formData.subject || undefined,
          edu_version: formData.edu_version ? parseInt(formData.edu_version) : undefined
        };
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '시험 객체 생성에 실패했습니다.');
      }

      const result = await response.json();
      
      if (isBulkMode()) {
        alert(`대량 생성이 완료되었습니다. 생성된 객체 수: ${result.data.count}개`);
      } else {
        alert(`시험 객체가 생성되었습니다. ID: ${result.data.sxm_id}`);
      }
      
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating exam object:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      school_name: '',
      att_year: '',
      school_grade: '',
      phase: '',
      subject: '',
      edu_version: '',
      school_type: ''
    });
    onClose();
  };

  // 현재 선택된 학교 타입에 따른 학년 옵션
  const currentSchoolType = formData.school_type || getSchoolTypeFromName(formData.school_name);
  const gradeOptions = getGradeOptions(currentSchoolType);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="시험 객체 생성"
      size="lg"
      preventScroll={true}
      zIndex={9999980}
      isMobileView={window.innerWidth < 768}
      className="w-[95vw] lg:w-[960px]"
      bodyClassName="overflow-y-auto"
      style={{
        height: window.innerWidth < 768 ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh',
        maxHeight: window.innerWidth < 768 ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={(!isSingleMode() && !isBulkMode()) || isLoading}
            loading={isLoading}
            className="bg-primary hover:bg-[#3da575]"
          >
            {isBulkMode() ? '대량 생성' : 
             isSingleMode() ? '단일 생성' : '생성하기'}
          </Button>
        </>
      }
    >
      <div className="mb-6 rounded border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 font-semibold text-blue-800">생성 모드 안내</h3>
        <div className="space-y-1 text-sm text-blue-700">
          <p><strong>단일 생성:</strong> 학교명, 출제년도, 학년, 시험구분을 모두 입력하면 1개 객체 생성</p>
          <p><strong>대량 생성:</strong> 학교명, 출제년도, 학교타입만 입력하면 해당 학교의 전체 학년/시험 조합으로 자동 생성</p>
          <p className="mt-2 text-xs text-blue-600">
            ※ 현재 입력 모드: <span className="font-semibold">
            {isBulkMode() ? '대량 생성 모드' : isSingleMode() ? '단일 생성 모드' : '입력 중...'}
            </span>
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* 공통 필드 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학교명/문제집명 <span className="text-red-500">*</span>
          </label>
          <SchoolSearchInput
            value={formData.school_name}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, school_name: value }));
            }}
            onSchoolSelect={(school, abbreviation) => {
              setSelectedSchool(school);
              setFormData(prev => ({ 
                ...prev, 
                school_name: school.school_name,  // 전체 학교명 저장
                school_type: school.school_level === '초등학교' ? '1' :
                            school.school_level === '중학교' ? '2' : '3'
              }));
            }}
            onWorkbookChange={(name) => {
              setFormData(prev => ({ ...prev, school_name: name, school_type: '' }));
            }}
            placeholder="학교명 검색"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            출제년도 <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="att_year"
            value={formData.att_year}
            onChange={handleInputChange}
            placeholder="예: 2025"
            className="w-full rounded border p-2"
            min="2016"
            max="2030"
            required
          />
        </div>

        {/* 대량 생성용 필드 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학교타입 <span className="text-blue-500">(대량 생성시 필수)</span>
          </label>
          <select
            name="school_type"
            value={formData.school_type}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
          >
            <option value="">학교타입 선택</option>
            {schoolTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <hr className="my-4" />

        {/* 단일 생성용 필드 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학년 <span className="text-green-500">(단일 생성시 필수)</span>
          </label>
          <select
            name="school_grade"
            value={formData.school_grade}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
          >
            <option value="">학년 선택</option>
            {gradeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            시험구분 <span className="text-green-500">(단일 생성시 필수)</span>
          </label>
          <select
            name="phase"
            value={formData.phase}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
          >
            <option value="">시험구분 선택</option>
            {phases.map(phase => (
              <option key={phase.value} value={phase.value}>{phase.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            과목 (선택)
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder="예: 수학, 영어, 국어..."
            className="w-full rounded border p-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            교육과정 (선택)
          </label>
          <input
            type="number"
            name="edu_version"
            value={formData.edu_version}
            onChange={handleInputChange}
            placeholder="예: 2022 (2022 개정 교육과정)"
            className="w-full rounded border p-2"
          />
          <p className="mt-1 text-xs text-gray-500">
            미입력 시 출제년도에 따라 자동 계산됩니다.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default ObjectModal; 